import React, { useState } from 'react';
import pluralize from 'pluralize';
import { useQuery } from 'react-query';
import {
  Box,
  Spinner,
  Alert,
  Badge,
  Divider,
  Heading,
  Text,
  CheckCircleIcon,
  CloseIcon,
  RefreshIcon,
  InfoIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  Dropdown,
  DropdownOverlay,
  SelectInput,
  ActionList,
  ActionListItem,
} from '@razorpay/blade/components';
import api from 'api';
import { aiWorkflowQueryKeys } from 'api/aiWorkflows';
import { WorkflowExecution, ExecutionStatus } from 'api/types/aiWorkflowTypes';
import { showToastViaEvent } from 'utils/ToastEvents';
import { AppError } from 'utils/AppError';
import { ExecutionCard, LogStep, ClickableBox } from '../../styles';
import { EmptyState } from '../EmptyState';
import { formatDate, formatEntityType, getDecisionBackgroundColor } from '../../utils';
import { STATUS_OPTIONS, ENTITY_TYPE_OPTIONS, DAYS_OPTIONS } from '../../constants';

const getStatusBadge = (status: ExecutionStatus, decision?: string) => {
  if (status === 'completed' && decision) {
    switch (decision) {
      case 'APPROVED':
        return <Badge color="positive">Approved</Badge>;
      case 'REJECTED':
        return <Badge color="negative">Rejected</Badge>;
      case 'MANUAL_REVIEW':
        return <Badge color="notice">Manual Review</Badge>;
      default:
        return <Badge color="positive">Completed</Badge>;
    }
  }

  switch (status) {
    case 'pending':
      return <Badge color="neutral">Pending</Badge>;
    case 'running':
      return <Badge color="information">Running</Badge>;
    case 'completed':
      return <Badge color="positive">Completed</Badge>;
    case 'failed':
      return <Badge color="negative">Failed</Badge>;
    default:
      return <Badge color="neutral">{status}</Badge>;
  }
};

const ExecutionRow = ({ execution }: { execution: WorkflowExecution }) => {
  const [isExpanded, setIsExpanded] = useState(false);

  const contextData = execution.context_data || {};
  const aiDecision = execution.ai_decision_result;
  const logs = execution.execution_logs || [];

  return (
    <ExecutionCard onClick={() => setIsExpanded(!isExpanded)}>
      <Box display="flex" justifyContent="space-between" alignItems="center">
        <Box display="flex" alignItems="center" gap="spacing.4">
          {getStatusBadge(execution.status, aiDecision?.decision)}
          <Box>
            <Text weight="semibold">
              {formatEntityType(execution.trigger_entity_type)} - {execution.trigger_entity_id}
            </Text>
            <Text size="small" color="surface.text.gray.muted">
              {(contextData.employee_name as string) || 'Unknown'} • {formatDate(execution.created_at)}
            </Text>
          </Box>
        </Box>
        <Box display="flex" alignItems="center" gap="spacing.3">
          {aiDecision && (
            <Box
              padding={['spacing.1', 'spacing.3']}
              backgroundColor="surface.background.gray.moderate"
              borderRadius="medium">
              <Text size="small">Confidence: {aiDecision.confidence}</Text>
            </Box>
          )}
          {isExpanded ? (
            <ChevronUpIcon size="medium" color="surface.icon.gray.muted" />
          ) : (
            <ChevronDownIcon size="medium" color="surface.icon.gray.muted" />
          )}
        </Box>
      </Box>

      {isExpanded && (
        <ClickableBox onClick={(e) => e.stopPropagation()}>
          <Box marginTop="spacing.4">
            <Divider />

            {Object.keys(contextData).length > 0 && (
              <Box marginTop="spacing.4">
                <Text size="small" weight="semibold" marginBottom="spacing.2">
                  Request Details
                </Text>
                <Box
                  padding="spacing.3"
                  backgroundColor="surface.background.gray.subtle"
                  borderRadius="medium">
                  {Object.entries(contextData).map(([key, value]) => (
                    <Box key={key} display="flex" gap="spacing.2" marginBottom="spacing.1">
                      <Text size="small" color="surface.text.gray.muted">
                        {key.replace(/_/g, ' ')}:
                      </Text>
                      <Text size="small">{String(value)}</Text>
                    </Box>
                  ))}
                </Box>
              </Box>
            )}

            {aiDecision && (
              <Box marginTop="spacing.4">
                <Text size="small" weight="semibold" marginBottom="spacing.2">
                  AI Decision
                </Text>
                <Box
                  padding="spacing.3"
                  backgroundColor={getDecisionBackgroundColor(aiDecision.decision)}
                  borderRadius="medium">
                  <Box
                    display="flex"
                    justifyContent="space-between"
                    alignItems="center"
                    marginBottom="spacing.2">
                    <Text size="small" weight="semibold">
                      {aiDecision.decision.replace(/_/g, ' ')}
                    </Text>
                    <Text size="small" color="surface.text.gray.muted">
                      {aiDecision.confidence} confidence
                    </Text>
                  </Box>
                  <Text size="small" color="surface.text.gray.muted">
                    {aiDecision.reasoning}
                  </Text>
                </Box>
              </Box>
            )}

            {execution.error_message && (
              <Box marginTop="spacing.4">
                <Text size="small" weight="semibold" marginBottom="spacing.2">
                  Error
                </Text>
                <Box
                  padding="spacing.3"
                  backgroundColor="surface.background.gray.moderate"
                  borderRadius="medium">
                  <Text size="small" color="feedback.text.negative.intense">
                    {execution.error_message}
                  </Text>
                </Box>
              </Box>
            )}

            {logs.length > 0 && (
              <Box marginTop="spacing.4">
                <Text size="small" weight="semibold" marginBottom="spacing.2">
                  Execution Steps
                </Text>
                <Box>
                  {logs.map((log, index) => (
                    <LogStep key={index} status={log.status}>
                      <Box>
                        {log.status === 'success' && (
                          <CheckCircleIcon size="small" color="feedback.icon.positive.intense" />
                        )}
                        {log.status === 'failed' && (
                          <CloseIcon size="small" color="feedback.icon.negative.intense" />
                        )}
                        {log.status === 'warning' && (
                          <InfoIcon size="small" color="feedback.icon.notice.intense" />
                        )}
                        {log.status === 'running' && (
                          <RefreshIcon size="small" color="feedback.icon.information.intense" />
                        )}
                      </Box>
                      <Box flex="1">
                        <Text size="small" weight="medium">
                          {log.step.replace(/_/g, ' ')}
                        </Text>
                        {log.message && (
                          <Text size="small" color="surface.text.gray.muted">
                            {log.message}
                          </Text>
                        )}
                        <Text size="xsmall" color="surface.text.gray.muted">
                          {formatDate(log.timestamp)}
                        </Text>
                      </Box>
                    </LogStep>
                  ))}
                </Box>
              </Box>
            )}

            <Box marginTop="spacing.4" display="flex" gap="spacing.6">
              {execution.started_at && (
                <Box>
                  <Text size="xsmall" color="surface.text.gray.muted">
                    Started
                  </Text>
                  <Text size="small">{formatDate(execution.started_at)}</Text>
                </Box>
              )}
              {execution.completed_at && (
                <Box>
                  <Text size="xsmall" color="surface.text.gray.muted">
                    Completed
                  </Text>
                  <Text size="small">{formatDate(execution.completed_at)}</Text>
                </Box>
              )}
            </Box>
          </Box>
        </ClickableBox>
      )}
    </ExecutionCard>
  );
};

const ActivityView = () => {
  const [statusFilter, setStatusFilter] = useState<ExecutionStatus | ''>('');
  const [entityTypeFilter, setEntityTypeFilter] = useState<string>('');
  const [daysFilter, setDaysFilter] = useState<number>(30);

  const {
    data: executionsData,
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: [
      aiWorkflowQueryKeys.executions,
      { status: statusFilter, trigger_entity_type: entityTypeFilter, days: daysFilter },
    ],
    queryFn: () =>
      api.aiWorkflows.getExecutions({
        status: statusFilter || undefined,
        trigger_entity_type: entityTypeFilter || undefined,
        days: daysFilter,
        per_page: 50,
      }),
    onError: (err: AppError) => {
      showToastViaEvent({
        text: err.message || 'Failed to load activity',
        type: 'danger',
        timeout: 3000,
      });
    },
  });

  const executions = executionsData?.data || [];
  const totalExecutions = executionsData?.total || 0;

  if (isLoading) {
    return (
      <Box display="flex" alignItems="center" justifyContent="center" height="100%" width="100%">
        <Spinner size="large" accessibilityLabel="Loading activity..." />
      </Box>
    );
  }

  if (error) {
    return (
      <Box padding="spacing.6">
        <Alert
          color="negative"
          description="Failed to load activity. Please try again."
          isDismissible
          onDismiss={() => {}}
          actions={{
            primary: {
              text: 'Retry',
              onClick: () => refetch(),
            },
          }}
        />
      </Box>
    );
  }

  if (executions.length === 0) {
    return (
      <EmptyState
        title="No activity yet"
        description="Workflow activity will appear here once you have active workflows running."
      />
    );
  }

  const selectedStatusLabel =
    STATUS_OPTIONS.find((o) => o.value === statusFilter)?.label || 'All statuses';
  const selectedEntityLabel =
    ENTITY_TYPE_OPTIONS.find((o) => o.value === entityTypeFilter)?.label || 'All types';
  const selectedDaysLabel =
    DAYS_OPTIONS.find((o) => o.value === String(daysFilter))?.label || 'Last 30 days';

  return (
    <Box padding="spacing.6" height="100%" overflowY="auto">
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        marginBottom="spacing.5">
        <Box>
          <Heading size="medium">Activity Log</Heading>
          <Text size="small" color="surface.text.gray.muted">
            {totalExecutions} {pluralize('execution', totalExecutions)} in the last {daysFilter} days
          </Text>
        </Box>

        <Box display="flex" gap="spacing.3">
          <Dropdown selectionType="single">
            <SelectInput label="" accessibilityLabel="Status filter" value={selectedStatusLabel} />
            <DropdownOverlay>
              <ActionList>
                {STATUS_OPTIONS.map((option) => (
                  <ActionListItem
                    key={option.value}
                    title={option.label}
                    value={option.value}
                    isSelected={statusFilter === option.value}
                    onClick={() => setStatusFilter(option.value as ExecutionStatus | '')}
                  />
                ))}
              </ActionList>
            </DropdownOverlay>
          </Dropdown>

          <Dropdown selectionType="single">
            <SelectInput
              label=""
              accessibilityLabel="Entity type filter"
              value={selectedEntityLabel}
            />
            <DropdownOverlay>
              <ActionList>
                {ENTITY_TYPE_OPTIONS.map((option) => (
                  <ActionListItem
                    key={option.value}
                    title={option.label}
                    value={option.value}
                    isSelected={entityTypeFilter === option.value}
                    onClick={() => setEntityTypeFilter(option.value)}
                  />
                ))}
              </ActionList>
            </DropdownOverlay>
          </Dropdown>

          <Dropdown selectionType="single">
            <SelectInput label="" accessibilityLabel="Days filter" value={selectedDaysLabel} />
            <DropdownOverlay>
              <ActionList>
                {DAYS_OPTIONS.map((option) => (
                  <ActionListItem
                    key={option.value}
                    title={option.label}
                    value={option.value}
                    isSelected={String(daysFilter) === option.value}
                    onClick={() => setDaysFilter(Number(option.value))}
                  />
                ))}
              </ActionList>
            </DropdownOverlay>
          </Dropdown>
        </Box>
      </Box>

      <Box>
        {executions.map((execution) => (
          <ExecutionRow key={execution.id} execution={execution} />
        ))}
      </Box>
    </Box>
  );
};

export { ActivityView };
